import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Navbar, CustomButton, LoadingSpinner } from '../components';
import { paymentsAPI, projectsAPI, authAPI, avatarsAPI, refreshSessionTokens } from '../services/api';
import { resolveInviteePostAuthRoute } from '../utils/freeTrial';

/** Survives history.replaceState stripping ?token= (otherwise orderId becomes "" and capture never runs). */
const PAYPAL_SUCCESS_ORDER_TOKEN_KEY = 'paypal_success_order_token';

function invitePostCaptureKeys(orderId) {
  return {
    lock: `paypal_invite_postcapture_lock_${orderId}`,
    done: `paypal_invite_postcapture_done_${orderId}`,
  };
}

async function navigateAfterInviteePayment(navigate, fallbackRoute = '/chat') {
  try {
    const storedInvite = JSON.parse(localStorage.getItem('signup_invite_params') || '{}');
    const isInvitee = !!(storedInvite.avatar_id || storedInvite.invite_id);
    if (!isInvitee) {
      navigate(fallbackRoute, { replace: true });
      return;
    }
    try {
      sessionStorage.removeItem('user_fetched');
      sessionStorage.removeItem('user_data');
    } catch (_) { /* ignore */ }
    let meData = null;
    try {
      const meRes = await authAPI.getCurrentUser();
      meData = meRes?.data || meRes;
    } catch (_) { /* ignore */ }
    const uid = meData?.id || meData?.user_id;
    const route = await resolveInviteePostAuthRoute({
      userData: meData,
      userId: uid,
      avatarsAPI,
      inviteId: storedInvite.invite_id,
      iWillPay: storedInvite.i_will_pay,
      avatarId: storedInvite.avatar_id,
    });
    navigate(route || '/chat', { replace: true });
  } catch (_) {
    navigate(fallbackRoute, { replace: true });
  }
}

/** Ensures send-invitation after capture runs at most once per order (Strict Mode / overlapping effects). */
async function acquirePostCaptureInviteSlot(orderId) {
  const { lock, done } = invitePostCaptureKeys(orderId);
  const deadline = Date.now() + 120000;
  while (Date.now() < deadline) {
    try {
      if (sessionStorage.getItem(done) === '1') {
        return { proceed: false, alreadyDone: true };
      }
      if (!sessionStorage.getItem(lock)) {
        sessionStorage.setItem(lock, '1');
        if (sessionStorage.getItem(done) === '1') {
          sessionStorage.removeItem(lock);
          return { proceed: false, alreadyDone: true };
        }
        return { proceed: true, alreadyDone: false };
      }
    } catch (_) {
      return { proceed: true, alreadyDone: false };
    }
    await new Promise((r) => setTimeout(r, 60));
  }
  try {
    if (sessionStorage.getItem(done) === '1') {
      return { proceed: false, alreadyDone: true };
    }
  } catch (_) { /* ignore */ }
  return { proceed: false, alreadyDone: false, timedOut: true };
}

function releasePostCaptureInviteLock(orderId) {
  try {
    sessionStorage.removeItem(invitePostCaptureKeys(orderId).lock);
  } catch (_) { /* ignore */ }
}

function markPostCaptureInvitesDone(orderId) {
  try {
    sessionStorage.setItem(invitePostCaptureKeys(orderId).done, '1');
  } catch (_) { /* ignore */ }
}

function readInviteModalCheckoutFromStorage() {
  try {
    const raw =
      sessionStorage.getItem('paypal_checkout_context') ||
      localStorage.getItem('paypal_checkout_context');
    if (!raw) return false;
    return Boolean(JSON.parse(raw).invite_modal_checkout);
  } catch (_) {
    return false;
  }
}

async function trySendGiftInvitesFromLocalStorage(
  orderId,
  setIsGiftPayment,
  setInviteModalGiftInviteOk
) {
  const slot = await acquirePostCaptureInviteSlot(orderId);
  if (!slot.proceed) {
    if (slot.alreadyDone && readInviteModalCheckoutFromStorage()) {
      setInviteModalGiftInviteOk(true);
    }
    return;
  }

  const pendingInviteStorageKey = `paypal_post_capture_invite_${orderId}`;
  let pendingRaw;
  try {
    pendingRaw = localStorage.getItem(pendingInviteStorageKey);
  } catch (_) {
    releasePostCaptureInviteLock(orderId);
    return;
  }
  if (!pendingRaw) {
    markPostCaptureInvitesDone(orderId);
    releasePostCaptureInviteLock(orderId);
    return;
  }
  let parsed;
  try {
    parsed = JSON.parse(pendingRaw);
  } catch (_) {
    markPostCaptureInvitesDone(orderId);
    releasePostCaptureInviteLock(orderId);
    return;
  }
  const inviteModalContext = readInviteModalCheckoutFromStorage();

  const parts =
    Array.isArray(parsed.gift_invite_parts) && parsed.gift_invite_parts.length > 0
      ? parsed.gift_invite_parts.filter(
          (p) => Array.isArray(p?.invite_emails) && p.invite_emails.length > 0
        )
      : null;

  if (!parts && (!Array.isArray(parsed.invite_emails) || parsed.invite_emails.length === 0)) {
    markPostCaptureInvitesDone(orderId);
    releasePostCaptureInviteLock(orderId);
    return;
  }

  try {
    localStorage.removeItem(pendingInviteStorageKey);
  } catch (_) { /* ignore */ }

  setIsGiftPayment(true);
  try {
    if (parts && parts.length > 0) {
      let sent = 0;
      for (const part of parts) {
        await projectsAPI.sendInvitation({
          invite_emails: [...part.invite_emails],
          plan_id: part.plan_id ?? 1,
          plan_pricing_id: part.plan_pricing_id ?? null,
          i_will_pay: part.i_will_pay ?? 1,
        });
        sent += part.invite_emails.length;
      }
    } else {
      await projectsAPI.sendInvitation({
        invite_emails: parsed.invite_emails,
        plan_id: parsed.plan_id ?? 1,
        plan_pricing_id: parsed.plan_pricing_id ?? null,
        i_will_pay: parsed.i_will_pay ?? 1,
      });
    }
    if (inviteModalContext) setInviteModalGiftInviteOk(true);
    markPostCaptureInvitesDone(orderId);
  } catch (inviteErr) {
    console.error('Bulk send-invitation after payment failed:', inviteErr);
    toast.error(
      inviteErr?.message ||
        'Payment succeeded but sending invitations failed. Please contact support.'
    );
    if (inviteModalContext) setInviteModalGiftInviteOk(false);
    try {
      localStorage.setItem(pendingInviteStorageKey, pendingRaw);
    } catch (_) { /* ignore */ }
  } finally {
    releasePostCaptureInviteLock(orderId);
  }
}

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [captureDone, setCaptureDone] = useState(false);
  const [captureData, setCaptureData] = useState(null);
  const [startedFrom, setStartedFrom] = useState(() => {
    try {
      const storedInvite = JSON.parse(localStorage.getItem('signup_invite_params') || '{}');
      const isInvitee = !!(storedInvite.avatar_id || storedInvite.invite_id);
      return isInvitee ? '/chat' : '/create-avatar';
    } catch (_) {
      return '/create-avatar';
    }
  });
  const [isPopup, setIsPopup] = useState(false);
  const [isGiftPayment, setIsGiftPayment] = useState(false);
  const [inviteModalCheckout, setInviteModalCheckout] = useState(false);
  /** True only after successful send-invitation for invite-modal gift checkout (for opener redirect). */
  const [inviteModalGiftInviteOk, setInviteModalGiftInviteOk] = useState(false);

  const orderId = useMemo(() => {
    const fromParams = searchParams.get('token') || '';
    if (fromParams) return fromParams;
    try {
      return sessionStorage.getItem(PAYPAL_SUCCESS_ORDER_TOKEN_KEY) || '';
    } catch (_) {
      return '';
    }
  }, [searchParams]);

  useLayoutEffect(() => {
    const t = searchParams.get('token') || '';
    if (!t) return;
    try {
      sessionStorage.setItem(PAYPAL_SUCCESS_ORDER_TOKEN_KEY, t);
    } catch (_) { /* ignore */ }
  }, [searchParams]);

  useEffect(() => {
    // Remove query params from URL to prevent re-running capture on refresh/back
    if (orderId && window?.history?.replaceState) {
      window.history.replaceState({}, '', '/payment/success');
    }
  }, [orderId]);

  useEffect(() => {
    const runCapture = async () => {
      if (!orderId) {
        toast.error('Missing PayPal order token.');
        try {
          sessionStorage.removeItem(PAYPAL_SUCCESS_ORDER_TOKEN_KEY);
        } catch (_) { /* ignore */ }
        navigate('/create-avatar', { replace: true });
        return;
      }

      setInviteModalGiftInviteOk(false);

      // Refresh session before protected capture (access token often expires during PayPal redirect)
      await refreshSessionTokens();

      const token = localStorage.getItem('access_token');
      if (!token) {
        toast.error('Session expired. Please sign in to finalize your payment.');
        navigate('/signin', {
          state: { from: '/payment/success', paypalOrderId: orderId },
          replace: true
        });
        return;
      }

      // Prefer the context whose order_id matches the current PayPal token.
      // A stale sessionStorage entry (e.g. from a prior popup payment) must not shadow
      // the correct localStorage entry written for this payment.
      const rawCtx = (() => {
        const fromSession = sessionStorage.getItem('paypal_checkout_context');
        const fromLocal = localStorage.getItem('paypal_checkout_context');
        for (const raw of [fromSession, fromLocal]) {
          if (!raw) continue;
          try {
            const parsed = JSON.parse(raw);
            // If ctx carries an order_id, only accept it when it matches; no order_id = legacy, accept.
            if (!parsed.order_id || String(parsed.order_id) === String(orderId)) return raw;
          } catch (_) {}
        }
        return fromSession || fromLocal || null;
      })();
      if (!rawCtx) {
        try {
          const lookup = await paymentsAPI.getInvoiceByPaypalOrder(orderId);
          if (lookup?.data) {
            setCaptureDone(true);
            await trySendGiftInvitesFromLocalStorage(
              orderId,
              setIsGiftPayment,
              setInviteModalGiftInviteOk
            );
            try {
              sessionStorage.removeItem(PAYPAL_SUCCESS_ORDER_TOKEN_KEY);
            } catch (_) { /* ignore */ }
            setLoading(false);
            return;
          }
        } catch (_) { }

        toast.error('Payment session expired. Please contact support if symptoms persist.');
        try {
          sessionStorage.removeItem(PAYPAL_SUCCESS_ORDER_TOKEN_KEY);
        } catch (_) { /* ignore */ }
        navigate('/create-avatar', { replace: true });
        return;
      }

      let ctx = null;
      try {
        ctx = JSON.parse(rawCtx);
      } catch (_) {
        toast.error('Invalid checkout context. Please restart payment.');
        navigate('/create-avatar', { replace: true });
        return;
      }

      if (ctx?.started_from) {
        setStartedFrom(ctx.started_from);
      }

      if (ctx?.context === 'popup') {
        setIsPopup(true);
      }

      setInviteModalCheckout(Boolean(ctx?.invite_modal_checkout));

      const avatarPlanId = ctx?.avatar_plan_id;
      const planInterval = ctx?.plan_interval;
      const autoRenew = Boolean(ctx?.auto_renew);
      const firstMonthDiscount = Boolean(ctx?.first_month_discount);
      const annualDiscount = Boolean(ctx?.annual_discount);
      const captureTotalUsd =
        ctx?.total_usd != null && Number.isFinite(Number(ctx.total_usd))
          ? Number(ctx.total_usd)
          : ctx?.bundle_total_usd != null && Number.isFinite(Number(ctx.bundle_total_usd))
            ? Number(ctx.bundle_total_usd)
            : undefined;
      const captureGiftLineItems =
        Array.isArray(ctx?.gift_line_items) && ctx.gift_line_items.length > 0
          ? ctx.gift_line_items
          : undefined;

      if (!avatarPlanId || !planInterval) {
        toast.error('Checkout data is incomplete. Please restart payment.');
        navigate('/create-avatar', { replace: true });
        return;
      }

      const pendingInviteStorageKey = `paypal_post_capture_invite_${orderId}`;

      const clearCheckoutInviteFields = () => {
        try {
          localStorage.removeItem(pendingInviteStorageKey);
        } catch (_) { /* ignore */ }
        [sessionStorage, localStorage].forEach((storage) => {
          const raw = storage.getItem('paypal_checkout_context');
          if (!raw) return;
          try {
            const updated = JSON.parse(raw);
            delete updated.pending_gift_invite_bulk;
            delete updated.pending_invites;
            delete updated.pending_invite;
            delete updated.reopen_invite_modal;
            storage.setItem('paypal_checkout_context', JSON.stringify(updated));
          } catch (_) { /* ignore */ }
        });
      };

      const sendPostCaptureInvitations = async () => {
        const slot = await acquirePostCaptureInviteSlot(orderId);
        if (!slot.proceed) {
          if (slot.alreadyDone && ctx?.invite_modal_checkout) {
            setInviteModalGiftInviteOk(true);
          }
          return;
        }

        try {
          try {
            if (sessionStorage.getItem(invitePostCaptureKeys(orderId).done) === '1') {
              if (ctx?.invite_modal_checkout) setInviteModalGiftInviteOk(true);
              return;
            }
          } catch (_) { /* ignore */ }

          let giftBulk = null;
          let fromLocalStoragePending = false;
          try {
            const pendingRaw = localStorage.getItem(pendingInviteStorageKey);
            if (pendingRaw) {
              const parsed = JSON.parse(pendingRaw);
              if (
                Array.isArray(parsed.gift_invite_parts) &&
                parsed.gift_invite_parts.some(
                  (p) => Array.isArray(p?.invite_emails) && p.invite_emails.length > 0
                )
              ) {
                giftBulk = { gift_invite_parts: parsed.gift_invite_parts };
                fromLocalStoragePending = true;
              } else if (Array.isArray(parsed.invite_emails) && parsed.invite_emails.length > 0) {
                giftBulk = {
                  invite_emails: parsed.invite_emails,
                  plan_id: parsed.plan_id ?? 1,
                  plan_pricing_id: parsed.plan_pricing_id ?? null,
                  i_will_pay: parsed.i_will_pay ?? 1,
                };
                fromLocalStoragePending = true;
              }
            }
          } catch (_) { /* ignore */ }

          if (!giftBulk) {
            giftBulk = ctx?.pending_gift_invite_bulk;
          }

          const pendingInvites =
            ctx?.pending_invites ||
            (ctx?.pending_invite?.invite_email ? [ctx.pending_invite] : []);

          const giftParts =
            giftBulk?.gift_invite_parts &&
            Array.isArray(giftBulk.gift_invite_parts) &&
            giftBulk.gift_invite_parts.filter(
              (p) => Array.isArray(p?.invite_emails) && p.invite_emails.length > 0
            );

          if (giftParts && giftParts.length > 0) {
            try {
              if (fromLocalStoragePending) {
                localStorage.removeItem(pendingInviteStorageKey);
              }
            } catch (_) { /* ignore */ }
            clearCheckoutInviteFields();

            setIsGiftPayment(true);
            try {
              let sent = 0;
              for (const part of giftParts) {
                await projectsAPI.sendInvitation({
                  invite_emails: [...part.invite_emails],
                  plan_id: part.plan_id ?? 1,
                  plan_pricing_id: part.plan_pricing_id ?? null,
                  i_will_pay: part.i_will_pay ?? 1,
                });
                sent += part.invite_emails.length;
              }
              if (ctx?.invite_modal_checkout) setInviteModalGiftInviteOk(true);
              markPostCaptureInvitesDone(orderId);
            } catch (inviteErr) {
              console.error('Bulk send-invitation after payment failed:', inviteErr);
              toast.error(
                inviteErr?.message ||
                  'Payment succeeded but sending invitations failed. Please contact support.'
              );
              if (ctx?.invite_modal_checkout) setInviteModalGiftInviteOk(false);
              try {
                if (fromLocalStoragePending) {
                  localStorage.setItem(
                    pendingInviteStorageKey,
                    JSON.stringify({ gift_invite_parts: giftParts })
                  );
                }
              } catch (_) { /* ignore */ }
            }
            return;
          }

          if (giftBulk && Array.isArray(giftBulk.invite_emails) && giftBulk.invite_emails.length > 0) {
            const emailsSnapshot = [...giftBulk.invite_emails];
            const planId = giftBulk.plan_id ?? 1;
            const planPricingId = giftBulk.plan_pricing_id ?? null;
            const iWillPay = giftBulk.i_will_pay ?? 1;

            try {
              if (fromLocalStoragePending) {
                localStorage.removeItem(pendingInviteStorageKey);
              }
            } catch (_) { /* ignore */ }
            clearCheckoutInviteFields();

            setIsGiftPayment(true);
            try {
              await projectsAPI.sendInvitation({
                invite_emails: emailsSnapshot,
                plan_id: planId,
                plan_pricing_id: planPricingId,
                i_will_pay: iWillPay,
              });
              if (ctx?.invite_modal_checkout) setInviteModalGiftInviteOk(true);
              markPostCaptureInvitesDone(orderId);
            } catch (inviteErr) {
              console.error('Bulk send-invitation after payment failed:', inviteErr);
              toast.error(
                inviteErr?.message ||
                  'Payment succeeded but sending invitations failed. Please contact support.'
              );
              if (ctx?.invite_modal_checkout) setInviteModalGiftInviteOk(false);
              try {
                if (fromLocalStoragePending) {
                  localStorage.setItem(
                    pendingInviteStorageKey,
                    JSON.stringify({
                      invite_emails: emailsSnapshot,
                      plan_id: planId,
                      plan_pricing_id: planPricingId,
                      i_will_pay: iWillPay,
                    })
                  );
                }
              } catch (_) { /* ignore */ }
            }
            return;
          }

          if (pendingInvites.length > 0) {
            setIsGiftPayment(true);
            clearCheckoutInviteFields();
            let anyFailed = false;
            for (const invite of pendingInvites) {
              try {
                await projectsAPI.sendInvitation({
                  invite_email: invite.invite_email,
                  plan_id: invite.plan_id,
                  plan_pricing_id: invite.plan_pricing_id,
                  i_will_pay: 1,
                });
              } catch (inviteErr) {
                anyFailed = true;
                console.error('Send invitation after payment failed:', inviteErr);
                toast.error(
                  inviteErr?.message ||
                    `Payment succeeded but sending invitation to ${invite.invite_email} failed. Please contact support.`
                );
              }
            }
            if (!anyFailed && pendingInvites.length > 1) {
            }
            if (ctx?.invite_modal_checkout) {
              setInviteModalGiftInviteOk(!anyFailed && pendingInvites.length > 0);
            }
            if (!anyFailed) {
              markPostCaptureInvitesDone(orderId);
            }
            return;
          }

          markPostCaptureInvitesDone(orderId);
        } finally {
          releasePostCaptureInviteLock(orderId);
        }
      };

      const processedKey = `payment_processed_${orderId}`;
      const processingKey = `payment_processing_${orderId}`;

      // If already processed in this browser session, avoid duplicate capture call
      const alreadyProcessed = sessionStorage.getItem(processedKey) === 'true';
      if (alreadyProcessed) {
        let restored = false;
        const cached = sessionStorage.getItem('paypal_capture_result');
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            if (parsed?.order_id != null && String(parsed.order_id) === String(orderId)) {
              setCaptureData(parsed);
              setCaptureDone(true);
              restored = true;
            }
          } catch (_) { /* ignore */ }
        }
        if (!restored) {
          try {
            const lookup = await paymentsAPI.getInvoiceByPaypalOrder(orderId);
            const lookupData = lookup?.data || lookup;
            if (lookupData?.invoice_id) {
              const normalized = {
                order_id: orderId,
                avatar_plan_id: Number(avatarPlanId),
                plan_interval: String(planInterval),
                auto_renew: autoRenew,
                subscription_id: lookupData?.subscription_id || null,
                invoice_id: lookupData?.invoice_id || null,
                invoice_download_url: lookupData?.invoice_download_url || null,
              };
              setCaptureData(normalized);
              setCaptureDone(true);
              sessionStorage.setItem('paypal_capture_result', JSON.stringify(normalized));
            }
          } catch (_) {
            // Ignore; user can retry manually from UI
          }
        }
        await sendPostCaptureInvitations();
        try {
          sessionStorage.removeItem(PAYPAL_SUCCESS_ORDER_TOKEN_KEY);
        } catch (_) { /* ignore */ }
        setLoading(false);
        return;
      }

      // React Strict Mode runs this effect twice in dev: the second run must not exit with
      // loading=false and captureDone=false (that makes the opener think payment failed).
      if (sessionStorage.getItem(processingKey) === 'true') {
        const deadline = Date.now() + 120000;
        while (Date.now() < deadline && sessionStorage.getItem(processingKey) === 'true') {
          await new Promise((r) => setTimeout(r, 100));
        }
        let restored = false;
        if (sessionStorage.getItem(processedKey) === 'true') {
          const cached = sessionStorage.getItem('paypal_capture_result');
          if (cached) {
            try {
              const parsed = JSON.parse(cached);
              if (parsed?.order_id != null && String(parsed.order_id) === String(orderId)) {
                setCaptureData(parsed);
                setCaptureDone(true);
                restored = true;
              }
            } catch (_) { /* ignore */ }
          }
          if (!restored) {
            try {
              const lookup = await paymentsAPI.getInvoiceByPaypalOrder(orderId);
              const lookupData = lookup?.data || lookup;
              if (lookupData?.invoice_id) {
                const normalized = {
                  order_id: orderId,
                  avatar_plan_id: Number(avatarPlanId),
                  plan_interval: String(planInterval),
                  auto_renew: autoRenew,
                  subscription_id: lookupData?.subscription_id || null,
                  invoice_id: lookupData?.invoice_id || null,
                  invoice_download_url: lookupData?.invoice_download_url || null,
                };
                setCaptureData(normalized);
                setCaptureDone(true);
                sessionStorage.setItem('paypal_capture_result', JSON.stringify(normalized));
                sessionStorage.setItem(processedKey, 'true');
              }
            } catch (_) { /* ignore */ }
          }
        }
        if (sessionStorage.getItem(processedKey) === 'true') {
          await sendPostCaptureInvitations();
        }
        try {
          sessionStorage.removeItem(PAYPAL_SUCCESS_ORDER_TOKEN_KEY);
        } catch (_) { /* ignore */ }
        setLoading(false);
        return;
      }
      sessionStorage.setItem(processingKey, 'true');

      try {
        const captureQuantity = Number(ctx?.quantity) || 1;
        const captureRes = await paymentsAPI.capturePaypalOrder({
          order_id: orderId,
          avatar_plan_id: avatarPlanId,
          plan_interval: planInterval,
          auto_renew: autoRenew,
          first_month_discount: firstMonthDiscount,
          annual_discount: annualDiscount,
          quantity: captureQuantity,
          ...(captureTotalUsd != null ? { total_usd: captureTotalUsd } : {}),
          ...(captureGiftLineItems ? { gift_line_items: captureGiftLineItems } : {}),
        });

        const payload = captureRes?.data || captureRes;
        const subscriptionId = payload?.subscription_id;
        const invoiceId = payload?.invoice_id;
        const invoiceDownloadUrl = payload?.invoice_download_url;

        const normalized = {
          order_id: orderId,
          avatar_plan_id: Number(avatarPlanId),
          plan_interval: String(planInterval),
          auto_renew: autoRenew,
          subscription_id: subscriptionId || null,
          invoice_id: invoiceId || null,
          invoice_download_url: invoiceDownloadUrl || null,
        };

        setCaptureData(normalized);
        setCaptureDone(true);
        sessionStorage.setItem('paypal_capture_result', JSON.stringify(normalized));
        sessionStorage.setItem(processedKey, 'true');

        await sendPostCaptureInvitations();
      } catch (error) {
        console.error('Capture failed:', error);
        // Optional restore helper: invoice lookup by paypal order id.
        try {
          const lookup = await paymentsAPI.getInvoiceByPaypalOrder(orderId);
          const lookupData = lookup?.data || lookup;
          if (lookupData?.invoice_id) {
            const normalized = {
              order_id: orderId,
              avatar_plan_id: Number(avatarPlanId),
              plan_interval: String(planInterval),
              auto_renew: autoRenew,
              subscription_id: lookupData?.subscription_id || null,
              invoice_id: lookupData?.invoice_id || null,
              invoice_download_url: lookupData?.invoice_download_url || null,
            };
            setCaptureData(normalized);
            setCaptureDone(true);
            sessionStorage.setItem('paypal_capture_result', JSON.stringify(normalized));
            sessionStorage.setItem(processedKey, 'true');
            await sendPostCaptureInvitations();
          } else {
            throw error;
          }
        } catch (_) {
          toast.error(error?.message || 'Payment capture failed. Please try again.');
        }
      } finally {
        sessionStorage.removeItem(processingKey);
        try {
          sessionStorage.removeItem(PAYPAL_SUCCESS_ORDER_TOKEN_KEY);
        } catch (_) { /* ignore */ }
        setLoading(false);
      }
    };

    runCapture();
  }, [navigate, orderId]);

  useEffect(() => {
    if (!isGiftPayment || !captureDone || isPopup) return;
    if (inviteModalCheckout && inviteModalGiftInviteOk) {
      navigate('/chat', { replace: true });
    } else {
      navigate(startedFrom, { replace: true });
    }
  }, [
    isGiftPayment,
    captureDone,
    isPopup,
    inviteModalCheckout,
    inviteModalGiftInviteOk,
    navigate,
    startedFrom,
  ]);

  useEffect(() => {
    if (!captureDone || isGiftPayment || isPopup) return;
    const storedInvite = (() => {
      try {
        return JSON.parse(localStorage.getItem('signup_invite_params') || '{}');
      } catch (_) {
        return {};
      }
    })();
    const isInvitee = !!(storedInvite.avatar_id || storedInvite.invite_id);
    if (isInvitee) {
      navigateAfterInviteePayment(navigate, startedFrom);
      return;
    }
    navigate(startedFrom, { replace: true });
  }, [captureDone, isGiftPayment, isPopup, navigate, startedFrom]);

  useEffect(() => {
    if (!loading) {
      const shouldClose = isPopup || (window.opener && window.opener !== window) || window.name === 'PayPal' || window.name === 'PayPalGift';

      const handleClose = () => {
        try {
          if (window.opener) {
            window.opener.postMessage({
              type: captureDone ? 'PAYPAL_SUCCESS' : 'PAYPAL_ERROR',
              data: captureData,
              error: captureDone ? null : 'Payment confirmation failed',
              inviteModalDoneGoChat: Boolean(
                captureDone && inviteModalCheckout && inviteModalGiftInviteOk
              ),
            }, '*');
          }
          if (shouldClose) {
            window.close();
          } else {
            navigate(inviteModalCheckout && inviteModalGiftInviteOk ? '/chat' : startedFrom, { replace: true });
          }
        } catch (e) {
          console.error('Failed to close success state:', e);
        }
      };

      if (shouldClose) {
        handleClose();
      }
    }
  }, [
    captureDone,
    loading,
    navigate,
    startedFrom,
    captureData,
    isPopup,
    inviteModalCheckout,
    inviteModalGiftInviteOk,
  ]);

  if (loading) {
    return (
      <div className="create-avatar-page">
        <Navbar disabled={true} />
        <LoadingSpinner fullScreen text="Finalizing your payment..." />
      </div>
    );
  }

  if (captureDone) return null;

  // Capture failed — not a popup, loading finished, capture did not succeed
  return (
    <div className="create-avatar-page">
      <Navbar disabled={true} />
      <div style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(255,255,255,0.96)',
        zIndex: 9999,
        padding: '24px',
      }}>
        <div style={{
          background: '#fff',
          borderRadius: '16px',
          boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
          padding: '56px 48px',
          maxWidth: '480px',
          width: '100%',
          textAlign: 'center',
        }}>
          <h2 style={{ marginBottom: '16px', fontSize: '1.75rem', fontWeight: 700, color: '#1a1a1a' }}>
            Something went wrong.
          </h2>
          <p style={{ color: '#555', marginBottom: '40px', fontSize: '1.05rem', lineHeight: '1.7' }}>
            We couldn't confirm your payment. Please contact support if this continues.
          </p>
          <CustomButton
            type="button"
            variant="secondary"
            onClick={() => navigate(startedFrom, { replace: true })}
            style={{ minWidth: '160px' }}
          >
            Go Back
          </CustomButton>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
